export interface BoardRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BoardViewport {
  x: number;
  y: number;
  zoom: number;
}

/**
 * Converts a screen-space rect (client coordinates, as returned by
 * `getBoundingClientRect`) into canvas coordinates.
 *
 * Svelte Flow's viewport transform is relative to the flow container, so
 * client coordinates must first be reduced by the container's own offset
 * before applying the inverse viewport transform — mirroring how the library
 * maps pointer events (see `pointToRendererPoint` in @xyflow/system).
 */
export function screenRectToCanvasRect(
  rect: { left: number; top: number; width: number; height: number },
  containerRect: { left: number; top: number },
  viewport: BoardViewport,
): BoardRect {
  return {
    x: (rect.left - containerRect.left - viewport.x) / viewport.zoom,
    y: (rect.top - containerRect.top - viewport.y) / viewport.zoom,
    width: rect.width / viewport.zoom,
    height: rect.height / viewport.zoom,
  };
}

const PLACEMENT_GAP = 24;
const MAX_SCAN_SLOTS = 200;
const MIN_BATCH_COLUMNS = 2;
const MAX_BATCH_COLUMNS = 8;

function overlaps(a: BoardRect, b: BoardRect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/**
 * Plans non-overlapping grid placements for a batch of new canvas items.
 * The working occupied list includes each placement as it is selected, so a
 * batch cannot calculate the same first-free slot for every item.
 */
export function planPlacements(
  count: number,
  size: { width: number; height: number },
  occupied: BoardRect[],
  viewport: BoardViewport,
  canvasSize: { width: number; height: number },
): BoardRect[] {
  if (count <= 0) return [];

  const originX = (-viewport.x + 40) / viewport.zoom;
  const originY = (-viewport.y + 80) / viewport.zoom;
  const visibleWidth = canvasSize.width / viewport.zoom;
  const stepX = size.width + PLACEMENT_GAP;
  const stepY = size.height + PLACEMENT_GAP;
  const naturalColumns = Math.max(1, Math.floor(visibleWidth / stepX));
  // Preserve the established single-item placement grid. Batches are bounded so
  // high zoom cannot collapse them to one column or place them in a very wide row.
  const columns =
    count === 1
      ? naturalColumns
      : Math.min(MAX_BATCH_COLUMNS, Math.max(MIN_BATCH_COLUMNS, naturalColumns));
  const candidateForSlot = (slot: number): BoardRect => ({
    x: originX + (slot % columns) * stepX,
    y: originY + Math.floor(slot / columns) * stepY,
    ...size,
  });
  const workingOccupied = [...occupied];
  const placements: BoardRect[] = [];
  let fallbackSlot = MAX_SCAN_SLOTS;

  for (let index = 0; index < count; index += 1) {
    let placement: BoardRect | undefined;
    for (let slot = 0; slot < MAX_SCAN_SLOTS; slot += 1) {
      const candidate = candidateForSlot(slot);
      if (!workingOccupied.some((existing) => overlaps(candidate, existing))) {
        placement = candidate;
        break;
      }
    }

    // Keep scanning after the capped primary grid until a free fallback slot is
    // found. This preserves the cap while avoiding both existing and batch items.
    if (!placement) {
      let fallback: BoardRect;
      do {
        fallback = candidateForSlot(fallbackSlot);
        fallbackSlot += 1;
      } while (workingOccupied.some((existing) => overlaps(fallback, existing)));
      placement = fallback;
    }

    placements.push(placement);
    workingOccupied.push(placement);
  }

  return placements;
}
